import { useInfiniteQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { fetchVerticalDocumentActivity } from 'services/verticalsApi'
import { DocumentActivityResponse, VerticalDocumentActivity } from 'store/verticals/types'
import { InfluencersFilters } from 'store/verticalsDetails/types'
import { Period, ElasticRange } from 'utils/types'

interface UseDocumentActivitiesQueryProps {
  entityId: string
  detailsFilters: InfluencersFilters
  zoneActivityIndices: string | null
  currentPeriod?: Period | null
  pageSize: number
  activityType?: string | string[]
  sort?: string | qs.ParsedQs | string[] | qs.ParsedQs[]
  additionalDataAddToRow?: Record<string, any>
  filterRange?: ElasticRange
}

export const useDocumentActivitiesQuery = ({
  entityId,
  detailsFilters,
  zoneActivityIndices,
  currentPeriod,
  pageSize,
  activityType,
  sort,
  additionalDataAddToRow,
  filterRange,
}: UseDocumentActivitiesQueryProps) => {
  const {
    data: verticalActivities,
    fetchNextPage,
    isFetching,
    isLoading,
  } = useInfiniteQuery<DocumentActivityResponse>({
    queryKey: [entityId, detailsFilters, zoneActivityIndices, currentPeriod, pageSize, activityType, sort, filterRange],
    queryFn: async ({ pageParam = 0 }) => {
      if (!zoneActivityIndices) return { items: [], total: 0 }
      const fromIndex = pageParam * pageSize
      return await fetchVerticalDocumentActivity(
        entityId,
        detailsFilters,
        fromIndex,
        zoneActivityIndices,
        currentPeriod,
        pageSize,
        activityType,
        sort,
        filterRange,
      )
    },
    getNextPageParam: (_lastGroup, groups) => groups.length,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  })

  const flatVerticalActivities: VerticalDocumentActivity[] | null = useMemo(() => {
    const flatEntities = verticalActivities?.pages.flatMap(page => page.items) || null
    return flatEntities?.length ? flatEntities.map(row => ({ ...row, additionalData: additionalDataAddToRow })) : null
  }, [verticalActivities, additionalDataAddToRow])

  const total = useMemo(() => {
    return verticalActivities?.pages?.[0]?.total ?? 0
  }, [verticalActivities])

  return {
    verticalDocumentActivity: flatVerticalActivities,
    fetchNextPage,
    isFetching,
    isLoading,
    totalFetched: flatVerticalActivities?.length || 0,
    total,
  }
}
